import { FC, useState, useRef, useEffect } from "react";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useRouter } from "next/router";

import {
  AddressType,
  cartApi,
  useCreateCheckoutMutation,
  useCreateOrderMutation,
} from "../../../../store";

import { Coupons } from "./Coupons";
import { PaymentMethod } from "./PaymentMethod";
import { PriceDetails } from "./PriceDetails";
import { useNotification } from "../../../hooks";
import { useTypedDispatch } from "../../../../hooks/useTypedDispatch";
import {
  CartProductEntity,
  CartTransformedResponse,
} from "../../../../types/cart_response_type";
import { CheckoutResponse } from "../../../../store/Services/OrderApi/Types";
import { OnlinePaymentMain } from "../../../organisms/PaymentDialog";
import { ConfirmDialog, PopupViewer } from "../../../molecules";
import { CreateOrderResponse } from "../../../../types/create_order_response_types";
import { analytics } from "../../../analytics";

interface Props {
  cartData: CartTransformedResponse;
  shippingAddress: AddressType;
  billingAddress: AddressType;
}

export const Payment: FC<Props> = ({
  billingAddress,
  shippingAddress,
  cartData,
}) => {
  const checkoutMethod = useRef<"POST" | "PUT">("POST");
  const orderDataRef = useRef<CreateOrderResponse>();

  const router = useRouter();
  const notify = useNotification();
  const dispatch = useTypedDispatch();

  const [checkoutData, setCheckoutData] = useState<CheckoutResponse>();
  const [paymentMethod, setPaymentMethod] = useState<"1" | "2">("1");
  const [priceRuleId, setPriceRuleId] = useState<number>(0);
  const [paymentUrl, setPaymentUrl] = useState<string>("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [checkout, { isLoading: checkoutLoading }] =
    useCreateCheckoutMutation();
  const [createOrder, { isLoading, isSuccess: ordered }] =
    useCreateOrderMutation();

  const currency =
    shippingAddress.country.toLowerCase() === "india" ? "INR" : "USD";

  useEffect(() => {
    (async () => {
      if (shippingAddress && billingAddress && !ordered) {
        notify<CheckoutResponse>(
          await checkout({
            payload: {
              billingAddressId: billingAddress.id,
              shippingAddressId: shippingAddress.id,
              paymentMethodId: Number(paymentMethod),
              priceruleId: priceRuleId,
            },
            method: checkoutMethod.current,
          }),
          { onSuccess: data => setCheckoutData(data) }
        );
        if (checkoutMethod.current === "POST") {
          checkoutMethod.current = "PUT";
        }
      }
    })();
  }, [
    priceRuleId,
    paymentMethod,
    shippingAddress,
    billingAddress,
    cartData,
    ordered,
  ]);

  useEffect(() => {
    if (checkoutData) {
      const priceItem =
        currency === "INR" ? cartData.value[1] : cartData.value[0];
      analytics.pushBeginCheckout({
        eventId: checkoutData.eventId,
        products: cartData.products.filter(
          p => !p.hasOwnProperty("errMessage")
        ) as CartProductEntity[],
        totalValue: priceItem.sellingPrice,
        cartId: cartData.cartId,
      });
      analytics.pushAddShippingInfo({
        eventId: checkoutData.eventId,
        address: shippingAddress!,
        products: cartData.products.filter(
          p => !p.hasOwnProperty("errMessage")
        ) as CartProductEntity[],
        totalValue: priceItem.sellingPrice,
      });
    }
  }, [checkoutData, shippingAddress]);

  useEffect(() => {
    if (checkoutMethod.current === "PUT" && checkoutData) {
      analytics.pushAddPaymentInfo({
        eventId: checkoutData.eventId,
        coupon: checkoutData.checkoutValue.coupon,
        currency: currency,
        paymentType: checkoutData.checkout.paymentMethodId.toString(),
        address: checkoutData.checkout.address.shippingAddress,
        totalValue: checkoutData.checkoutValue.total,
        discount: checkoutData.checkoutValue.discount,
        shippingCharges: checkoutData.checkoutValue.shipmentCharges,
        products: cartData.products as any,
      });
    }
  }, [paymentMethod, checkoutData]);

  const currencySymbol =
    shippingAddress.country.toLocaleLowerCase() === "india" ? "₹" : "$";

  const handleCreateOrder = async () => {
    notify<CreateOrderResponse>(await createOrder(), {
      onSuccess: orderData => {
        orderDataRef.current = orderData;
        dispatch(
          cartApi.util.updateQueryData("getCart", undefined, data => {
            data.products = [];
            data.count = 0;
            data.cartId = "";
            data.value = [
              { code: "INR", discount: 0, mrp: 0, sellingPrice: 0 },
              { code: "USD", discount: 0, mrp: 0, sellingPrice: 0 },
            ];
          })
        );
        if (orderData.result.paymentLink) {
          setPaymentUrl(orderData.result.paymentLink);
        } else {
          router.replace(
            "/checkout/thankyou?id=" +
              orderData.result.orderId +
              `&eventId=${orderData.result.eventId}`
          );
        }
      },
      successMessage: "Order created successfully",
    });
  };

  const closePaymentModal = () => {
    setPaymentUrl("");
    router.push(`/order-details/${orderDataRef.current!.result.orderId}`);
  };

  return (
    <>
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            textTransform="uppercase"
            fontWeight={700}>
            Payment
          </Typography>
          {checkoutLoading && <CircularProgress size={16} />}
        </Stack>
        <Coupons priceRuleId={priceRuleId} setPriceRuleId={setPriceRuleId} />
        <PaymentMethod
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          currency={currency}
        />
        {checkoutData && (
          <PriceDetails
            value={checkoutData.checkoutValue}
            currencySymbol={currencySymbol}
          />
        )}
        <Button
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
          disabled={isLoading}
          variant="contained"
          size="large"
          onClick={handleCreateOrder}>
          Place Order
        </Button>
      </Stack>
      {paymentUrl && (
        <PopupViewer
          open={Boolean(paymentUrl)}
          setOpen={value => setShowConfirmDialog(true)}>
          <OnlinePaymentMain
            open={Boolean(paymentUrl)}
            onClose={() => setShowConfirmDialog(true)}
            paymentURL={paymentUrl}
          />
        </PopupViewer>
      )}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Alert"
        message="Are you sure you want to cancel the payment?"
        onAgree={closePaymentModal}
        onDisagree={() => setShowConfirmDialog(false)}
      />
    </>
  );
};
